// OBSESSION homepage motion: the WebGL hero, the floating header and the
// scroll-driven ticker. Plain WebGL1, one quad, one texture, no libraries.
//
// The hero photo arrives through zebra-stripe shaped fronts (the garment's own
// print), then settles into a slow warp; the cursor carries a lens that bends
// the stripes and splits the colour channels. On touch screens the lens drifts
// by itself. The loop stops when the hero is off screen or the tab is hidden,
// and nothing runs at all for reduced motion or without WebGL — the <img>
// underneath is the page.
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ------------------------------------------------------------ header
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-solid', scrollY > 40);
    addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ------------------------------------------------------------ ticker
  // Drifts on its own, and runs faster (and backwards) with scroll velocity.
  const ticker = document.querySelector('.marquee');
  if (ticker && !reduce) {
    const track = ticker.querySelector('.marquee__track');
    ticker.classList.add('is-js');
    let x = 0, boost = 0, lastY = scrollY, visible = false, raf = 0;
    const tick = () => {
      const dy = scrollY - lastY; lastY = scrollY;
      boost += (dy * 0.6 - boost) * 0.1;
      x -= 0.6 + boost;
      const half = track.scrollWidth / 2;
      if (x <= -half) x += half;
      if (x > 0) x -= half;
      track.style.transform = `translate3d(${x}px,0,0)`;
      raf = visible ? requestAnimationFrame(tick) : 0;
    };
    new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible && !raf) { lastY = scrollY; raf = requestAnimationFrame(tick); }
    }).observe(ticker);
  }

  // ------------------------------------------------------------ hero
  const hero = document.querySelector('[data-hero]');
  if (!hero) return;
  const reveal = () => hero.classList.add('is-in');
  const img = hero.querySelector('[data-hero-img]');
  const canvas = hero.querySelector('[data-hero-canvas]');
  if (reduce || !img || !canvas) { reveal(); return; }

  const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' });
  if (!gl) { reveal(); return; }

  const VERT = `attribute vec2 p; varying vec2 v; void main(){ v = p * .5 + .5; gl_Position = vec4(p, 0., 1.); }`;
  const FRAG = `precision highp float;
    varying vec2 v;
    uniform sampler2D img;
    uniform vec2 res, size, mouse;
    uniform float t, intro, lensAmt;

    // cover-fit the photo, framed a little above centre
    vec2 cover(vec2 uv) {
      float rs = res.x / res.y, ri = size.x / size.y;
      vec2 s = rs > ri ? vec2(1., ri / rs) : vec2(rs / ri, 1.);
      return (uv - vec2(.5, .6)) * s + vec2(.5, .6);
    }
    // the print: warped, uneven bands like the halter's zebra stripe
    float stripe(vec2 q) {
      q.x += .32 * sin(q.y * 2.3 + t * .12) + .14 * sin(q.y * 6.1 - t * .09 + q.x * 1.7);
      return sin(q.x * 10. + q.y * 3.4 + .9 * sin(q.y * 3.7 + q.x * 2. + t * .16));
    }
    float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

    void main() {
      float aspect = res.x / res.y;
      vec2 q = (v - .5) * vec2(aspect, 1.);
      float z = stripe(q * 1.15);

      vec2 m = (mouse - .5) * vec2(aspect, 1.);
      float lens = smoothstep(.42, 0., distance(q, m)) * lensAmt;

      vec2 disp = vec2(z, -z * .6) * (.0018 + .012 * lens) + (q - m) * lens * -.035;
      float ca = .001 + .004 * lens;
      vec2 uv = cover(v + disp);
      vec3 col = vec3(
        texture2D(img, uv + vec2(ca, 0.)).r,
        texture2D(img, uv).g,
        texture2D(img, uv - vec2(ca, 0.)).b);

      // grade: deeper blacks, a little heat in the reds
      col = pow(col, vec3(1.08));
      col *= vec3(1.06, .96, .95);
      // the stripe glows faintly red where the lens passes
      col += vec3(.75, .03, .06) * smoothstep(.75, 1., z) * .08 * lens;

      // intro: the photo opens through stripe-shaped fronts, bottom-left first
      float sweep = intro * 1.9 - .45 + (v.x * .35 + (1. - v.y) * .25);
      float front = sweep - (z * .5 + .5) * .55;
      float mask = smoothstep(0., .06, front);
      float edge = smoothstep(0., .03, front) * (1. - smoothstep(.03, .09, front));

      float vig = smoothstep(1.3, .3, length((v - .5) * vec2(1.25, 1.)));
      col *= mix(.5, 1., vig);
      col += (hash(gl_FragCoord.xy + fract(t) * 91.) - .5) * .03;

      vec3 night = vec3(.043, .035, .035);
      col = mix(night, col, mask) + vec3(.9, .05, .08) * edge * (1. - intro * .6);
      gl_FragColor = vec4(col, 1.);
    }`;

  const compile = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  };
  let prog;
  try {
    prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
  } catch (err) { console.warn('[obsession] hero shader off:', err); reveal(); return; }
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const U = {};
  for (const n of ['img', 'res', 'size', 'mouse', 't', 'intro', 'lensAmt']) U[n] = gl.getUniformLocation(prog, n);

  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  for (const [k, val] of [[gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE], [gl.TEXTURE_MIN_FILTER, gl.LINEAR], [gl.TEXTURE_MAG_FILTER, gl.LINEAR]])
    gl.texParameteri(gl.TEXTURE_2D, k, val);

  let w = 0, h = 0;
  const resize = () => {
    const dpr = Math.min(devicePixelRatio || 1, 1.5);
    w = Math.round(hero.clientWidth * dpr); h = Math.round(hero.clientHeight * dpr);
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; gl.viewport(0, 0, w, h); }
  };

  // pointer: eased toward the cursor; on touch screens it wanders on its own
  const touch = matchMedia('(hover: none)').matches;
  const target = { x: .62, y: .55, a: touch ? .45 : 0 };
  const cur = { x: .62, y: .55, a: 0 };
  if (!touch) {
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      target.x = (e.clientX - r.left) / r.width; target.y = 1 - (e.clientY - r.top) / r.height; target.a = 1;
    });
    hero.addEventListener('pointerleave', () => { target.a = 0; });
  }

  let start = 0, visible = true, raf = 0, ready = false;
  const frame = (now) => {
    if (!start) start = now;
    const t = (now - start) / 1000;
    if (touch) { target.x = .5 + .28 * Math.sin(t * .35); target.y = .55 + .18 * Math.sin(t * .23 + 1); }
    cur.x += (target.x - cur.x) * .08; cur.y += (target.y - cur.y) * .08; cur.a += (target.a - cur.a) * .05;
    const intro = Math.min(1, t / 2.2);
    const eased = 1 - Math.pow(1 - intro, 3);
    gl.uniform2f(U.res, w, h);
    gl.uniform2f(U.mouse, cur.x, cur.y);
    gl.uniform1f(U.t, t);
    gl.uniform1f(U.intro, eased);
    gl.uniform1f(U.lensAmt, cur.a);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    raf = visible && !document.hidden ? requestAnimationFrame(frame) : 0;
  };
  const run = () => { if (ready && visible && !document.hidden && !raf) raf = requestAnimationFrame(frame); };

  new IntersectionObserver(([e]) => { visible = e.isIntersecting; run(); }).observe(hero);
  document.addEventListener('visibilitychange', run);
  addEventListener('resize', () => { resize(); run(); }, { passive: true });

  // Upload whichever source <picture> chose; a new one if the breakpoint flips.
  let loaded = '';
  const upload = () => {
    const src = img.currentSrc || img.src;
    if (!img.complete || !img.naturalWidth || src === loaded) return;
    loaded = src;
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img); }
    catch (err) { console.warn('[obsession] hero texture blocked:', err); reveal(); return; }
    gl.uniform1i(U.img, 0);
    gl.uniform2f(U.size, img.naturalWidth, img.naturalHeight);
    resize();
    if (!ready) {
      ready = true;
      hero.classList.add('is-gl');
      setTimeout(reveal, 500);
    }
    run();
  };
  img.addEventListener('load', upload);
  upload();
  setTimeout(() => { if (!ready) reveal(); }, 3500);
})();
